import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { ArrowRight } from 'lucide-react';
import { trackTrialCtaClick } from '../lib/analytics';

// Rotating words to keep some subtle motion in the hero.
const ROTATING_WORDS = {
  es: ['español', 'conversación', 'gramática', 'cultura'],
  en: ['Spanish', 'conversation', 'grammar', 'culture'],
  de: ['Spanisch', 'Konversation', 'Grammatik', 'Kultur'],
};

const TITLE = {
  es: 'Clases de español online para hablar con más seguridad y naturalidad',
  en: 'Private online Spanish lessons',
  de: 'Spanisch lernen mit einer Muttersprachlerin aus Spanien',
};

const LEAD = {
  es: 'Clases individuales 1 a 1 adaptadas a tu nivel, tus objetivos y tu ritmo. Aprende con Cecilia, profesora nativa, examinadora DELE y con más de 10 años de experiencia enseñando español.',
  en: 'One-to-one Spanish lessons with a native teacher based in Spain. Build real conversation skills, prepare the DELE with an accredited examiner, or learn the Spanish you need to live and work in Spain — wherever you are.',
  de: 'Spanisch im Einzelunterricht online: endlich sicher sprechen, das DELE mit einer akkreditierten Prüferin vorbereiten oder dich auf das Leben in Spanien vorbereiten. Persönlich, flexibel und immer 1:1.',
};

const SUBTITLE = {
  es: 'Todo lo que necesitas para aprender',
  en: 'Everything you need to learn',
  de: 'Alles, was du zum Lernen brauchst',
};

const CTA_MICROCOPY = {
  es: '30 minutos · Online · Sin compromiso',
  en: '30 minutes · Online · No commitment',
  de: '30 Minuten · Online · Ohne Verpflichtung',
};

const CTA_PRIMARY = {
  es: 'Reservar mi clase de prueba gratis',
  en: 'Book your free trial lesson',
  de: 'Kostenlose Probestunde buchen',
};
const CTA_SECONDARY = {
  es: 'Cómo funcionan las clases',
  en: 'How the lessons work',
  de: 'So läuft der Unterricht ab',
};

export const Hero = () => {
  const { language } = useLanguage();
  const lang = ROTATING_WORDS[language] ? language : 'es';
  const words = ROTATING_WORDS[lang];
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIndex((i) => (i + 1) % words.length), 2400);
    return () => clearInterval(t);
  }, [words.length]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="hero"
      className="relative min-h-[82vh] sm:min-h-screen flex items-center justify-center overflow-hidden bg-[#B7DAEE] pt-20 sm:pt-0"
    >
      {/* Subtle grid texture in the background for warmth */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.15] pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(#ffffff 1px, transparent 1px), linear-gradient(90deg, #ffffff 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}
      />

      {/* Pink gradient blend at the bottom to integrate with next section */}
      <div
        aria-hidden
        className="absolute inset-x-0 bottom-0 h-1/2 pointer-events-none"
        style={{
          background:
            'linear-gradient(to bottom, transparent 0%, rgba(255,201,200,0.35) 55%, rgba(255,201,200,0.75) 100%)',
        }}
      />
      {/* Corner pink glow for a soft warm accent */}
      <div
        aria-hidden
        className="absolute -bottom-32 -right-32 w-[60vw] h-[60vw] rounded-full pointer-events-none"
        style={{
          background:
            'radial-gradient(circle at center, rgba(245,166,164,0.35) 0%, transparent 70%)',
        }}
      />

      <div className="relative z-10 w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-16">
        {/* Central card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
          className="bg-[#EAF3FA] rounded-sm shadow-xl px-5 sm:px-12 md:px-20 py-8 sm:py-14 md:py-20 text-center"
          data-testid="hero-card"
        >
          {/* Logo top */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="flex flex-col items-center mb-5 sm:mb-10"
          >
            <img
              src="/logo.png"
              alt="Spanisch en la Red"
              className="w-16 h-16 sm:w-28 sm:h-28 object-contain mb-1"
            />
            <span
              className="text-[#E48A48] italic text-sm sm:text-base"
              style={{ fontFamily: '"Brush Script MT", "Segoe Script", cursive' }}
            >
              spanisch en la red
            </span>
          </motion.div>

          {/* Big grey title */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl font-extrabold text-[#4A4A4A] tracking-tight leading-[1.08] mb-4 sm:mb-6"
            data-testid="hero-title"
          >
            {TITLE[lang]}
          </motion.h1>

          {/* Lead paragraph right under the H1 */}
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="max-w-3xl mx-auto text-sm sm:text-base md:text-lg text-[#4A4A4A]/80 leading-relaxed mb-6 sm:mb-8"
            data-testid="hero-lead"
          >
            {LEAD[lang]}
          </motion.p>

          {/* Italic subtitle with rotating word */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="text-base sm:text-xl md:text-2xl italic text-[#4A4A4A]/80 mb-6 sm:mb-10 flex flex-wrap items-baseline justify-center gap-x-2"
            data-testid="hero-subtitle"
          >
            <span>{SUBTITLE[lang]}</span>
            <AnimatePresence mode="wait">
              <motion.span
                key={words[index]}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.45 }}
                className="font-semibold not-italic bg-gradient-to-r from-[#7DBDD9] to-[#E48A48] bg-clip-text text-transparent"
                data-testid="hero-rotating-word"
              >
                {words[index]}
              </motion.span>
            </AnimatePresence>
          </motion.div>

          {/* CTA buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => { trackTrialCtaClick('hero'); scrollTo('booking'); }}
              data-testid="hero-cta-primary"
              className="group px-8 py-3.5 bg-[#7DBDD9] hover:bg-[#5DA6C9] text-white rounded-full font-semibold text-base shadow-lg hover:shadow-xl transition-all flex items-center gap-2"
            >
              <span>{CTA_PRIMARY[lang]}</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => scrollTo('methodology')}
              data-testid="hero-cta-secondary"
              className="px-8 py-3.5 bg-white text-[#4A4A4A] rounded-full font-semibold text-base shadow-md hover:shadow-lg transition-all border border-gray-200 hover:border-[#7DBDD9]"
            >
              {CTA_SECONDARY[lang]}
            </motion.button>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-4 text-sm text-[#4A4A4A]/70"
            data-testid="hero-cta-microcopy"
          >
            {CTA_MICROCOPY[lang]}
          </motion.p>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2"
      >
        <div className="w-6 h-10 border-2 border-white/70 rounded-full flex items-start justify-center p-2">
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-1.5 h-1.5 bg-white rounded-full"
          />
        </div>
      </motion.div>
    </section>
  );
};
